import { defineNuxtPlugin } from '#app';
import { useUserStore } from '~/store/user/userStore';
import { useDialogStore } from '~/store/dialog/dialogStore';
import { useRoute, watch } from '#imports';
import { type ExportedGlobalComposer } from 'vue-i18n';
import { useSurveyService } from '~/composables/service/useSurveyService';
import { SurveyResponseModel } from '~~/package/mapper/survey/model/SurveyResponseModel';
import { resolveSdkDomain, resolveSdkUrl } from '~~/package/utils/SdkUrlUtil';
import { waitForSdk } from '~~/package/utils/SdkLoaderUtil';
import { isStorybookEnvironment } from '~~/package/utils/StorybookUtil';
import {
    createSdkReconciler,
    isAnyPopupOpen,
    resolveSdkLanguage,
    resolveSdkRole,
} from '~~/package/utils/SdkObserverUtil';
import { createCompletionCache, mapSurveyAnswers } from '~~/package/mapper/survey/SurveyHelper';

// 도메인별 S3 prefix(예: /localhost/, /eu.codepresso.io/)에서 SDK를 로드한다 (설정 데이터 경로와 동일 규칙).
const SURVEY_SDK_URL = resolveSdkUrl('survey-sdk.umd.cjs');

interface NuxtI18nCustom {
    locale: string | { value: string };
    setLocale: (locale: string) => void;
    getLocaleCookie: () => string;
}

interface NuxtAppCustom {
    $i18n: ExportedGlobalComposer & NuxtI18nCustom;
}

type SurveyAnswers = Record<string, number | string | string[]>;

/** onSubmit 으로 넘어오는 config 중 저장에 필요한 최소 형태 (survey-sdk SurveyConfig 부분집합) */
interface SurveyQuestionLite {
    id: string;
    type: string;
}
interface SurveyConfigLite {
    name: string;
    page: string;
    version: string;
    questions: SurveyQuestionLite[];
}

interface SurveyObserveHandle {
    readonly activeSurvey: { start: () => void; destroy: () => void } | null;
    setLocale: (locale: string) => void;
    destroy: () => void;
}

interface SurveySdkExports {
    Survey: {
        observe: (
            options?: {
                autoStart?: boolean;
                locale?: string;
                role?: string;
                onSubmit?: (
                    answers: SurveyAnswers,
                    config: SurveyConfigLite,
                    meta?: { jsonUrl?: string }
                ) => void;
                resolveCompleted?: (
                    jsonUrl: string,
                    version: string
                ) => boolean | Promise<boolean>;
            },
            domain?: string
        ) => Promise<SurveyObserveHandle>;
    };
    fetchSurveyForCurrentPage: (domain?: string, pathname?: string, role?: string) => Promise<unknown>;
    injectStyles?: () => void;
    removeStyles?: () => void;
}

export default defineNuxtPlugin(async (nuxtApp) => {
    if (isStorybookEnvironment()) return;

    const userStore = useUserStore();
    const dialogStore = useDialogStore();
    const route = useRoute();
    const $i18n = (nuxtApp as unknown as NuxtAppCustom).$i18n;

    // DI 컨테이너에서 1회 resolve. await 이전(= Nuxt 컨텍스트 보장)에 호출해야
    // 클라이언트에서 useNuxtApp() 컨텍스트 유실을 피한다.
    const { submitResponse, fetchCompletions } = useSurveyService();

    let sdk: SurveySdkExports;
    try {
        sdk = (await waitForSdk<typeof window.SurveySdk>(
            'SurveySdk',
            SURVEY_SDK_URL
        )) as unknown as SurveySdkExports;
    } catch (e) {
        console.warn('[SV] survey SDK load failed, skipping survey plugin:', e);
        return;
    }

    const { Survey, fetchSurveyForCurrentPage, injectStyles, removeStyles } = sdk;

    if (typeof injectStyles === 'function') {
        injectStyles();
    }

    const getLanguage = () => resolveSdkLanguage($i18n.locale);

    // 사용자 데이터가 로드되기 전에는 role을 undefined로 둔다 (6.onboardingSdk 와 동일).
    const getRole = (): string | undefined =>
        resolveSdkRole({
            uuid: userStore.userV2.uuid,
            isAdmin: userStore.isAdminType || userStore.isSuperAdminType,
        });

    // assessment id (= ProSkillAssure assessmentId / customAssessmentId). 두 설문 트리거 페이지 모두
    // 라우트의 [assessmentId] 세그먼트를 갖는다. 호출 시점에 현재 라우트에서 동기적으로 읽는다.
    const getAssessmentId = (): number | null => {
        const raw = route.params.assessmentId;
        const value = Array.isArray(raw) ? raw[0] : raw;
        const n = Number(value);
        return value !== undefined && Number.isFinite(n) ? n : null;
    };

    // 설문 설정(config)의 reExposureAfterDays 를 jsonUrl 기준으로 메모이즈해 조회한다.
    // SDK 의 resolveCompleted(jsonUrl, version) 은 이 값을 넘겨주지 않으므로 host 가 직접 config 를 읽는다.
    const reExposureCache = new Map<string, number | undefined>();
    const getReExposureAfterDays = async (jsonUrl: string): Promise<number | undefined> => {
        if (reExposureCache.has(jsonUrl)) return reExposureCache.get(jsonUrl);
        try {
            const config = (await fetchSurveyForCurrentPage(sdkDomain, undefined, getRole())) as
                | (SurveyConfigLite & { reExposureAfterDays?: number })
                | null
                | undefined;
            const days = config?.reExposureAfterDays;
            reExposureCache.set(jsonUrl, days);
            return days;
        } catch (e) {
            console.warn('[SV] survey config(reExposureAfterDays) 조회 실패:', e);
            return undefined;
        }
    };

    // ── 완료 게이트 캐시 ──────────────────────────────────────────────
    // 백엔드 기준 (surveyId + version + assessmentId) 완료 목록을 1회 로드해 메모이즈한다.
    // SDK observe 의 resolveCompleted 훅에서 동기적으로 조회되어 런처 노출을 결정한다.
    const completionCache = createCompletionCache(fetchCompletions);

    // 로그인 사용자에 한해 백엔드 완료 여부를 반영. 비로그인은 항상 false(미완료 취급).
    const resolveCompleted = async (jsonUrl: string, version: string): Promise<boolean> => {
        if (!userStore.userV2.uuid) return false;
        await completionCache.ensure();
        const assessmentId = getAssessmentId();
        const reExposureAfterDays = await getReExposureAfterDays(jsonUrl);
        return completionCache.has(jsonUrl, version, assessmentId, reExposureAfterDays);
    };

    // 설문 제출 → contact_us 저장. config.questions 기준으로 questionId/type/value 배열 구성(GA 키 보존).
    const onSubmit = (
        answers: SurveyAnswers,
        config: SurveyConfigLite,
        meta?: { jsonUrl?: string }
    ) => {
        if (!userStore.userV2.uuid) return; // 설문은 로그인 사용자 대상

        const surveyId = meta?.jsonUrl ?? config.name;
        const assessmentId = getAssessmentId();
        const model = SurveyResponseModel.create({
            surveyId,
            surveyName: config.name,
            version: config.version,
            page: config.page,
            locale: getLanguage(),
            assessmentId,
            answers: mapSurveyAnswers(answers, config.questions),
        });

        submitResponse(model)
            .then(() => {
                // 같은 기기 즉시 게이트(SDK localStorage)에 더해 메모이즈 Map 도 갱신.
                completionCache.add(surveyId, config.version, assessmentId);
            })
            .catch((e) => console.warn('[SV] survey submit 실패:', e));
    };

    // SDK 스크립트 URL 과 동일한 canonical 도메인. observe 에 명시적으로 넘기지 않으면 SDK 가
    // survey/index.json 등 config 를 raw window.location.hostname 으로 로드해(config-loader getDomain)
    // www.codepresso.io / 127.0.0.1 같은 host 에서 prefix 불일치로 404 가 난다.
    const sdkDomain = resolveSdkDomain();

    // 팝업(다이얼로그)이 하나라도 열려 있으면 true.
    const isPopupOpen = () =>
        isAnyPopupOpen({ show: dialogStore.show, multiOption: dialogStore.multiOption });

    // 현재 role/locale/팝업 상태에 맞춰 observer를 재구성한다 (6.onboardingSdk 와 동일 패턴, seq 가드는 createSdkReconciler 내부).
    const reconciler = createSdkReconciler<SurveyObserveHandle>({
        observe: () =>
            Survey.observe(
                {
                    autoStart: true,
                    locale: getLanguage(),
                    role: getRole(),
                    onSubmit,
                    resolveCompleted,
                },
                sdkDomain
            ),
        isPopupOpen,
        onError: (e) => console.warn('[SV] observe() 실패:', e),
    });
    const reconcile = () => reconciler.reconcile();

    // $i18n.locale 변경 감지 (사용자가 직접 언어 전환 시)
    watch(
        () => {
            const loc = $i18n.locale;
            return (typeof loc === 'string' ? loc : (loc as any)?.value ?? '') || 'en';
        },
        (newLocale) => {
            reconciler.setLocale(newLocale.toLowerCase());
        }
    );

    // userStore.userV2.locale 변경 감지 (회원 데이터 로드 후 locale 확정 시)
    watch(
        () => userStore.userV2.locale,
        () => {
            reconciler.setLocale(getLanguage());
        }
    );

    // 로그인 사용자(uuid) 또는 role 변경 감지 + 최초 1회 init.
    // uuid 를 키에 포함하므로 같은 role 의 다른 사용자로 교체돼도(A 로그아웃 → 같은 role B 로그인)
    // 완료 캐시를 비우고 observer 를 재구성해 B 기준 완료/노출 상태로 재평가한다.
    // (role 만 watch 하면 A→B 가 동일 role 일 때 발화하지 않아 A 의 상태가 잔존했음)
    watch(
        () => `${userStore.userV2.uuid}|${getRole() ?? ''}`,
        () => {
            completionCache.invalidate();
            reconcile();
        },
        { immediate: true }
    );

    // 팝업 노출 상태 감지: 팝업이 열리면 런처를 숨기고(destroy), 모두 닫히면 다시 observe.
    watch(() => isPopupOpen(), () => reconcile());

    return {
        provide: {
            surveySdk: {
                Survey,
                // 외부 호출자가 domain 을 생략하면 canonical prefix 를 기본 주입(observe 와 동일 규칙).
                fetchSurveyForCurrentPage: (domain?: string, pathname?: string, role?: string) =>
                    fetchSurveyForCurrentPage(domain ?? sdkDomain, pathname, role),
                injectStyles,
                removeStyles,
            },
        },
    };
});
